import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

const QueueContext = createContext(null);

export function QueueProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [count, setCount] = useState({ total: 0, by_app: { giraffe: 0, arx: 0, uncode: 0, cult: 0 } });
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelFilter, setPanelFilter] = useState(null); // null = all, or app name
  const intervalRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const { data } = await api.get('/queue/count');
      setCount(data);
    } catch {
      // silently ignore — badge just won't update
    }
  }, [isAuthenticated]);

  // Poll every 60 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchCount();
    intervalRef.current = setInterval(fetchCount, 60000);
    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, fetchCount]);

  // Re-fetch on window focus
  useEffect(() => {
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchCount]);

  const openQueue = useCallback((filter = null) => {
    setPanelFilter(filter);
    setPanelOpen(true);
  }, []);

  const closeQueue = useCallback(() => {
    setPanelOpen(false);
  }, []);

  const refreshCount = useCallback(() => {
    fetchCount();
  }, [fetchCount]);

  return (
    <QueueContext.Provider value={{ count, panelOpen, panelFilter, openQueue, closeQueue, refreshCount, setPanelFilter }}>
      {children}
    </QueueContext.Provider>
  );
}

const QUEUE_SAFE_DEFAULT = {
  count: { total: 0, by_app: { giraffe: 0, arx: 0, uncode: 0, cult: 0 } },
  panelOpen: false,
  panelFilter: null,
  openQueue: () => {},
  closeQueue: () => {},
  refreshCount: () => {},
  setPanelFilter: () => {},
};

export function useQueue() {
  const ctx = useContext(QueueContext);
  // On subdomains (giraffe., uncode., etc.) QueueProvider is not mounted —
  // return a safe no-op default so pages render without errors.
  return ctx || QUEUE_SAFE_DEFAULT;
}
